import { v4 as uuidv4 } from "uuid";
import { z } from "zod";
import { assertUnreachable } from "./utilities.js";
const plainBotParagraphFragmentSchema = z.object({
  type: z.literal("plain"),
  text: z.string()
});
const colorBotParagraphFragmentSchema = z.object({
  type: z.enum(["green", "red"]),
  text: z.string(),
  highlightIndex: z.number()
});
const botParagraphFragmentSchema = z.union([
  plainBotParagraphFragmentSchema,
  colorBotParagraphFragmentSchema
]);
const botParagraphSchema = z.object({
  type: z.literal("text"),
  fragments: z.array(botParagraphFragmentSchema)
});
const quoteParagraphSchema = z.object({
  type: z.literal("quote"),
  id: z.string(),
  short: z.string(),
  long: z.string(),
  startTime: z.string().optional()
});
const paragraphSchema = z.union([
  botParagraphSchema,
  quoteParagraphSchema
]);
function extractParagraphsFromValidation(text, highlights) {
  const paragraphs = [];
  let isLastParagraphClosed = false;
  const addFragment = (fragment) => {
    const lastParagraph = paragraphs.at(-1);
    if (!lastParagraph || lastParagraph.type !== "text" || isLastParagraphClosed) {
      const fixedText = fragment.text.replace(/^[\p{P}\p{S}\s]+/u, "").replace(/^[\p{L}]/u, (first) => first.toUpperCase());
      if (!fixedText) {
        return;
      }
      paragraphs.push({
        type: "text",
        fragments: [{ ...fragment, text: fixedText }]
      });
      isLastParagraphClosed = false;
      return;
    }
    lastParagraph.fragments.push(fragment);
  };
  let remainingHighlights = highlights;
  const addFragmentText = (fragmentText) => {
    let fragments = [
      { text: fragmentText, type: "plain" }
    ];
    const foundHighlights = [];
    for (const highlight of remainingHighlights) {
      const matchingFragmentIndex = fragments.findIndex(
        (f) => f.type === "plain" && f.text.indexOf(highlight.text) >= 0
      );
      const matchingFragment = fragments[matchingFragmentIndex];
      if (!matchingFragment) {
        continue;
      }
      const newFragments = [];
      const highlightIndex = matchingFragment.text.indexOf(highlight.text);
      const highlightEndIndex = highlightIndex + highlight.text.length;
      if (highlightIndex > 0) {
        newFragments.push({
          text: matchingFragment.text.slice(0, highlightIndex),
          type: "plain"
        });
      }
      newFragments.push({
        text: highlight.text,
        type: highlight.color,
        highlightIndex: highlights.indexOf(highlight)
      });
      if (highlightEndIndex < matchingFragment.text.length) {
        newFragments.push({
          text: matchingFragment.text.slice(highlightEndIndex),
          type: "plain"
        });
      }
      fragments = [
        ...fragments.slice(0, matchingFragmentIndex),
        ...newFragments,
        ...fragments.slice(matchingFragmentIndex + 1)
      ];
      foundHighlights.push(highlight);
    }
    fragments.forEach(addFragment);
    remainingHighlights = remainingHighlights.filter(
      (h) => !foundHighlights.includes(h)
    );
  };
  const updateLastFragmentForQuote = () => {
    const lastParagraph = paragraphs.at(-1);
    if (!lastParagraph || lastParagraph.type !== "text" || isLastParagraphClosed) {
      return;
    }
    const { fragments } = lastParagraph;
    let fragmentFound = false;
    let lastFragment;
    while (!fragmentFound && (lastFragment = fragments.at(-1))) {
      lastFragment.text = lastFragment.text.replace(/[\p{P}\p{S}\s]+$/u, "").replace(/[^:]$/, (last) => `${last}:`);
      if (!lastFragment.text) {
        fragments.pop();
      } else {
        fragmentFound = true;
      }
    }
  };
  const addQuote = (quoteText) => {
    const newQuotes = [];
    for (const part of quoteText.split(/\.\.\.|…/)) {
      const trimmedPart = part.trim();
      if (!trimmedPart) {
        continue;
      }
      const words = trimmedPart.split(" ");
      const short = words.length > 40 ? `${words.slice(0, 40).join(" ").trim()}...` : words.join(" ").trim();
      newQuotes.push({
        type: "quote",
        // Use unique ids to prevent collision when we group quotes from different messages together (like in AI chat for transcripts)
        id: uuidv4(),
        short,
        long: trimmedPart
      });
    }
    if (newQuotes.length === 0) {
      return;
    }
    updateLastFragmentForQuote();
    paragraphs.push(...newQuotes);
  };
  const splitRegex = /(<(?:[a-z]*$|[a-z]+>)|\n)/g;
  let match;
  let lastIndex = 0;
  while ((match = splitRegex.exec(text)) !== null) {
    const [matchText, tagOrNewline] = match;
    if (match.index > lastIndex) {
      addFragmentText(text.slice(lastIndex, match.index));
    }
    const restIndex = match.index + matchText.length;
    switch (tagOrNewline) {
      case "<quote>": {
        const endTagRegex = /<(?:$|(?:\/[a-z]*$)|(?:\/([a-z]+)>))/g;
        endTagRegex.lastIndex = restIndex;
        const endTagMatch = endTagRegex.exec(text);
        const endIndex = endTagMatch ? endTagMatch.index : text.length;
        addQuote(text.slice(restIndex, endIndex));
        lastIndex = endTagMatch ? endTagMatch.index + endTagMatch[0].length : endIndex;
        break;
      }
      case "\n": {
        isLastParagraphClosed = true;
        lastIndex = restIndex;
        break;
      }
      default: {
        lastIndex = restIndex;
        break;
      }
    }
    splitRegex.lastIndex = lastIndex;
  }
  if (text.length > lastIndex) {
    addFragmentText(text.slice(lastIndex));
  }
  return paragraphs;
}
function extractParagraphsFromMarkerAnalysis(text) {
  const paragraphs = [];
  const addTextParagraph = (content) => {
    const trimmed = content.trim().replace(/^[\p{P}\p{S}\s]+/u, "").replace(/^[\p{L}]/u, (first) => first.toUpperCase());
    if (!trimmed) return;
    paragraphs.push({
      type: "text",
      fragments: [{ type: "plain", text: trimmed }]
    });
  };
  const addQuote = (quoteText, startTime) => {
    for (const part of quoteText.split(/\.\.\.|…/)) {
      const trimmedPart = part.trim();
      if (!trimmedPart) continue;
      const words = trimmedPart.split(" ");
      const short = words.length > 40 ? `${words.slice(0, 40).join(" ").trim()}...` : words.join(" ").trim();
      paragraphs.push({
        type: "quote",
        id: uuidv4(),
        short,
        long: trimmedPart,
        startTime
      });
    }
  };
  const quoteRegex = /<quote(?:\s+[^>]*)?\s*>([\s\S]*?)<\/quote>/g;
  const startTimeAttrRegex = /start_time="(\[-?\d{2}:\d{2}:\d{2}\])"/;
  let lastIndex = 0;
  let match;
  while ((match = quoteRegex.exec(text)) !== null) {
    addTextParagraph(text.slice(lastIndex, match.index));
    const startTimeMatch = startTimeAttrRegex.exec(match[0]);
    addQuote(match[1] ?? "", startTimeMatch?.[1]);
    lastIndex = match.index + match[0].length;
  }
  addTextParagraph(text.slice(lastIndex));
  return paragraphs;
}
function formatGradingValidation(validation) {
  return validation.map((paragraph) => {
    switch (paragraph.type) {
      case "text":
        return paragraph.fragments.map((fragment) => {
          switch (fragment.type) {
            case "plain":
              return fragment.text;
            case "green":
              return `<green-highlight>${fragment.text}</green-highlight>`;
            case "red":
              return `<red-highlight>${fragment.text}</red-highlight>`;
            default:
              return assertUnreachable(fragment, () => "");
          }
        }).join("");
      case "quote":
        return paragraph.startTime ? `<quote start_time="${paragraph.startTime}">${paragraph.long}</quote>` : `<quote>${paragraph.long}</quote>`;
      default:
        return assertUnreachable(paragraph, () => "");
    }
  }).join("\n\n");
}
export {
  extractParagraphsFromMarkerAnalysis,
  extractParagraphsFromValidation,
  formatGradingValidation,
  paragraphSchema
};
//# sourceMappingURL=gradeValidationHelpers.js.map
